import type {ReactElement} from 'react';
import {useRef} from 'react';
import {PickerInputField} from '@Components/color-picker-v2/components/picker-input-field';
import {CanvasColorPicker} from '@Components/color-picker-v2/components/canvas-color-picker';
import styles from './input-section.module.scss';
import type {RGBA} from '@Utils/color.util';
import {hexToRgba} from '@Utils/color.util';
import {AlphaPercentagePickerInputField} from '@Components/color-picker-v2/components/alpha-percentage-picker-input-field/alpha-percentage-picker-input-field';
import {EyedropperColorPicker, EyeDropperCTAType} from '@Components/eyedropper-color-picker/eyedropper-color-picker';
import useEyeDropper from 'use-eye-dropper';

interface InputSectionProps {
  className?: string;
  color: RGBA;
  isAlphaDisabled?: boolean;

  onChangeColor(color: RGBA | null): void;

  onChangeEnded(color: RGBA | null): void;

  onChangeColorPickerMode?(state: boolean): void;

  showPicker?: boolean;
}

export function InputSection({
  isAlphaDisabled = false,
  className = '',
  onChangeColorPickerMode = (): void => {},
  showPicker = true,
  ...props
}: InputSectionProps): ReactElement | null {
  const containerRef = useRef<HTMLDivElement>(null);
  const {isSupported: isEyeDropperSupported} = useEyeDropper();

  const hasCanvasPages = (): boolean => {
    return (window.posterEditor?.whiteboard?.pages.getPagesInOrder() ?? []).length > 0;
  };

  const getColorPicker = (): ReactElement | null => {
    if (!showPicker) {
      return null;
    }

    if (isEyeDropperSupported()) {
      return (
        <EyedropperColorPicker
          onColorPicked={(val: string): void => {
            const color = hexToRgba(val);
            props.onChangeEnded(color);
          }}
          ctaType={EyeDropperCTAType.ICON}
        />
      );
    }

    if (hasCanvasPages()) {
      return (
        <CanvasColorPicker
          className={styles.picker}
          color={props.color}
          onChangeColor={(value: RGBA | null): void => {
            props.onChangeColor(value);
          }}
          onChangeEnded={(value: RGBA | null): void => {
            props.onChangeEnded(value);
          }}
          onChangeColorPickerMode={(state): void => {
            onChangeColorPickerMode(state);
          }}
        />
      );
    }

    return null;
  };

  return (
    <div ref={containerRef} className={`${className} spacing-m-t-5 flex-h-row`}>
      <div className={`${styles.inputContainer} radius-4 hover-transition-all`}>
        <div className={isAlphaDisabled ? styles.pickerInputWithoutAlpha : styles.pickerInput}>
          <PickerInputField
            inputContainerClassName={styles.inputContainerHeight}
            color={props.color}
            onChangeColor={(value: RGBA | null): void => {
              props.onChangeColor(value);
            }}
            onChangeEnded={(value: RGBA | null): void => {
              props.onChangeEnded(value);
            }}
            inputClassname={styles.pickerInputField}
          />
        </div>
        {!isAlphaDisabled ? <div className={`${styles.divider} _full-height`} /> : null}
        {!isAlphaDisabled ? (
          <div className={styles.alphaInput}>
            <AlphaPercentagePickerInputField
              className={styles.inputContainerHeight}
              color={props.color}
              onChangeColor={(value: RGBA | null): void => {
                props.onChangeColor(value);
              }}
              onChangeEnded={(value: RGBA | null): void => {
                props.onChangeEnded(value);
              }}
              inputClassName={styles.alphaInputField}
            />
          </div>
        ) : (
          []
        )}
      </div>
      {getColorPicker()}
    </div>
  );
}
